import type { Player, Enemy, Bullet, Pickup, Particle, Boss, Weapon } from './entities.js';
import type { PickupKind, Vec2 } from '../types.js';

export const MAX_BULLETS = 512;
export const MAX_PARTICLES = 1024;
export const MAX_ENEMIES = 128;
export const MAX_PICKUPS = 64;

export class World {
  worldWidth: number;
  worldHeight: number;

  tick = 0;
  timeMs = 0;

  players: Player[] = [];
  enemies: Enemy[] = [];
  bullets: Bullet[] = [];
  pickups: Pickup[] = [];
  particles: Particle[] = [];
  boss: Boss | null = null;
  /** Position history per formation group (for snake chain movement). */
  formationHistory: Map<string, Array<{ x: number; y: number }>> = new Map();

  shake = 0;
  hitStopMs = 0;

  hiScore = 0;
  phase: 'playing' | 'cleared' | 'gameover' = 'playing';

  private nextId = 1;

  constructor(worldWidth: number, worldHeight: number) {
    this.worldWidth = worldWidth;
    this.worldHeight = worldHeight;
  }

  mintId(): number {
    return this.nextId++;
  }

  spawnPlayer(x: number, y: number, weapon: Weapon): Player {
    const p: Player = {
      id: this.mintId(),
      alive: true,
      pos: { x, y },
      vel: { x: 0, y: 0 },
      radius: 12,
      hitboxRadius: 4.8, // ~40% of 12
      hp: 1,
      lives: 3,
      bombs: 3,
      invulnMs: 1500,
      respawnMs: 0,
      weapon,
      score: 0,
      shieldHp: 0,
    };
    this.players.push(p);
    return p;
  }

  spawnBullet(params: {
    pos: Vec2;
    vel: Vec2;
    team: 'player' | 'enemy';
    kind: Bullet['kind'];
    damage: number;
    pierce?: number;
    radius?: number;
    targetId?: number;
    armMs?: number;
    turnRate?: number;
  }): Bullet | null {
    const slot = this.findDeadSlot(this.bullets);
    const b: Bullet = {
      id: this.mintId(),
      alive: true,
      pos: { x: params.pos.x, y: params.pos.y },
      vel: { x: params.vel.x, y: params.vel.y },
      radius: params.radius ?? (params.kind === 'laser' ? 5 : 4),
      team: params.team,
      kind: params.kind,
      damage: params.damage,
      pierce: params.pierce ?? 0,
      ageMs: 0,
      targetId: params.targetId ?? -1,
      armMs: params.armMs ?? 0,
      turnRate: params.turnRate ?? 0,
    };
    if (slot === -1) {
      if (this.bullets.length >= MAX_BULLETS) return null;
      this.bullets.push(b);
    } else {
      this.bullets[slot] = b;
    }
    return b;
  }

  spawnEnemy(params: {
    defId: string;
    archetype: 'straight' | 'sine' | 'aimed' | 'swarm' | 'snake' | 'orbit';
    pos: Vec2;
    vel: Vec2;
    radius: number;
    hp: number;
    scoreValue: number;
    fireRateHz?: number;
    bulletSpeed?: number;
    amplitude?: number;
    frequency?: number;
    group?: string;
    swarmIndex?: number;
  }): Enemy | null {
    const slot = this.findDeadSlot(this.enemies);
    const e: Enemy = {
      id: this.mintId(),
      alive: true,
      defId: params.defId,
      archetype: params.archetype,
      pos: { x: params.pos.x, y: params.pos.y },
      vel: { x: params.vel.x, y: params.vel.y },
      radius: params.radius,
      hp: params.hp,
      hpMax: params.hp,
      scoreValue: params.scoreValue,
      flashMs: 0,
      fireRateHz: params.fireRateHz ?? 0,
      bulletSpeed: params.bulletSpeed ?? 0,
      fireCooldownMs: 0,
      spawnTimeMs: this.timeMs,
      spawnX: params.pos.x,
      amplitude: params.amplitude ?? 0,
      frequency: params.frequency ?? 0,
      group: params.group ?? '',
      swarmIndex: params.swarmIndex ?? 0,
      pathPhase: 0,
    };
    if (slot === -1) {
      if (this.enemies.length >= MAX_ENEMIES) return null;
      this.enemies.push(e);
    } else {
      this.enemies[slot] = e;
    }
    return e;
  }

  spawnPickup(params: { pos: Vec2; fallSpeed: number; weapon: PickupKind }): Pickup | null {
    const slot = this.findDeadSlot(this.pickups);
    const pk: Pickup = {
      id: this.mintId(),
      alive: true,
      pos: { x: params.pos.x, y: params.pos.y },
      vel: { x: 0, y: params.fallSpeed },
      radius: 14,
      ageMs: 0,
      weaponAtSpawn: params.weapon,
    };
    if (slot === -1) {
      if (this.pickups.length >= MAX_PICKUPS) return null;
      this.pickups.push(pk);
    } else {
      this.pickups[slot] = pk;
    }
    return pk;
  }

  spawnParticle(params: {
    pos: Vec2;
    vel: Vec2;
    lifeMs: number;
    color: number;
    size: number;
  }): Particle | null {
    const slot = this.findDeadSlot(this.particles);
    const p: Particle = {
      id: this.mintId(),
      alive: true,
      pos: { x: params.pos.x, y: params.pos.y },
      vel: { x: params.vel.x, y: params.vel.y },
      ageMs: 0,
      lifeMs: params.lifeMs,
      color: params.color,
      size: params.size,
    };
    if (slot === -1) {
      if (this.particles.length >= MAX_PARTICLES) return null;
      this.particles.push(p);
    } else {
      this.particles[slot] = p;
    }
    return p;
  }

  spawnBoss(params: {
    defId: string;
    pos: Vec2;
    radius: number;
    hp: number;
  }): Boss {
    const b: Boss = {
      id: this.mintId(),
      alive: true,
      defId: params.defId,
      pos: { x: params.pos.x, y: params.pos.y },
      vel: { x: 0, y: 0 },
      radius: params.radius,
      hp: params.hp,
      hpMax: params.hp,
      phase: 0,
      flashMs: 0,
      fireCooldownMs: 0,
      spiralAngle: 0,
      ageMs: 0,
    };
    this.boss = b;
    return b;
  }

  despawnBullet(b: Bullet): void {
    b.alive = false;
  }
  despawnEnemy(e: Enemy): void {
    e.alive = false;
  }
  despawnPickup(p: Pickup): void {
    p.alive = false;
  }
  despawnParticle(p: Particle): void {
    p.alive = false;
  }
  despawnBoss(): void {
    if (this.boss) this.boss.alive = false;
  }

  countAliveBullets(): number {
    let n = 0;
    for (const b of this.bullets) if (b.alive) n++;
    return n;
  }

  private findDeadSlot<T extends { alive: boolean }>(arr: T[]): number {
    for (let i = 0; i < arr.length; i++) {
      if (!arr[i].alive) return i;
    }
    return -1;
  }
}
